// Core packages
import {
  Component, Inject,
  OnDestroy, OnInit
} from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { Subscription } from "rxjs";

// Custom packages
import { IUser } from "src/app/shared/models/user/user.interface";
import { User } from "src/app/shared/models/user/user.model";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";

@Component({
  selector: "app-add-balance-dialog",
  templateUrl: "./add-balance-dialog.component.html",
  styleUrls: ["./add-balance-dialog.component.scss"]
})
export class AddBalanceDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  user: User = new User();
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  availableBalances = [];
  searchAll = true;
  previousValue: any = {};
  previousTotValue: any = {};

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.add"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  // END - Add subUser form

  dialogIsLoading = true;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<AddBalanceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public configService: ConfigService,

    private cookieService: CookieService,
    private translateService: TranslateService,
    private apiService: ApiService
  ) {
    dialogRef.disableClose = true;
  }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    // Get current lang
    this.lang = this.cookieService.get("lang") || this.configService.defaultLang;

    if (this.data.availableOptions) {
      this.availableBalances = this.data.availableOptions;
    }

    // Init add form
    this.initForm();

    // Stop loading dialog
    this.dialogIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Handle click on Denial button
   *
   * @since 1.0.0
   */
  onDenial(): void {
    this.dialogRef.close();
  }

  /**
   * Handle click on confirm button
   *
   * @since 1.0.0
   */
  onConfirm(): void {
    if (this.form.invalid) {
      // Workaround to force mat-error display on untouched fields
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        if (control.invalid) {
          control.markAsTouched();
          control.markAsDirty();
        }
      });
      return;
    }
    this.dialogRef.close(this.model);
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = {
      code: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      vat: null,
      totalRow: 0,
    };

    this.fields = [];

    // Set fields
    this.fields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: 'col-12',
            type: 'select',
            key: 'code',
            templateOptions: {
              required: true,
              label: this.translateService.instant('balances.fields.detail.code'),
              placeholder: this.translateService.instant('balances.fields.detail.code'),
              options: this.availableBalances,
              onChange: '',
            },
            expressionProperties: {
              "templateOptions.onChange": (value: any) => {
                this.updateFields(value);
              },
            }
          },
          {
            className: 'col-12 col-md-4',
            type: 'input',
            key: 'quantity',
            templateOptions: {
              required: true,
              type: 'number',
              label: this.translateService.instant('balances.fields.detail.quantity'),
              placeholder: this.translateService.instant('balances.fields.detail.quantity'),
              min: 1,
            },
            defaultValue: 1,
            expressionProperties: {
              "templateOptions.onChange": (value: any) => {
                this.updateTotal(value);
              }
            }
          },
          {
            className: 'col-12 col-md-4',
            type: 'input',
            key: 'unitPrice',
            templateOptions: {
              required: true,
              type: 'number',
              label: this.translateService.instant('balances.fields.detail.unitPrice'),
              placeholder: this.translateService.instant('balances.fields.detail.unitPrice'),
              min: 0,
              disabled: this.model.status && this.model.status !== 'open',
            },
            defaultValue: 0,
            expressionProperties: {
              "templateOptions.onChange": (value: any) => {
                this.updateTotal(value);
              }
            }
          },
          {
            className: 'col-12 col-md-4',
            type: 'input',
            key: 'totalRow',
            templateOptions: {
              type: 'number',
              label: this.translateService.instant('balances.fields.detail.totalRow'),
              placeholder: this.translateService.instant('balances.fields.detail.totalRow'),
              readonly: true,
              min: 0,
            },
          },
          {
            className: 'col-12',
            type: 'textarea',
            key: 'description',
            templateOptions: {
              label: this.translateService.instant('balances.fields.detail.description'),
              placeholder: this.translateService.instant('balances.fields.detail.description'),
              rows: 5,
            },
          },
        ]
      }
    ];
  }

  /**
   * Autofill fields based on code changes
   *
   * @since 1.0.0
   */
  updateFields(value: any): void {
    if (value !== this.previousValue) {
      this.previousValue = value;
      const codeControl = this.form.controls.code;
      const descriptionControl = this.form.controls.description;

      // Watch company type value changes
      if (codeControl) {
        codeControl.valueChanges
          .subscribe(res => {
            if (!res) {
              return;
            }
            if (res) {
              const newBalanceRow = this.availableBalances.find(element => {
                return element.value === res.toString();
              });
              if (newBalanceRow && newBalanceRow.description) {
                const description = newBalanceRow.description.replace(/\s\s+/g, ' ');
                descriptionControl.setValue(description);
              }
            }
          });
      }
    }
    return;
  }

  /**
   * Watch for value changes and update total value
   *
   * @since 1.0.0
   */
  updateTotal(value: any) {
    if (value !== this.previousTotValue) {
      this.previousTotValue = value;
      const quantityControl = this.form.controls.quantity;
      const unitPriceControl = this.form.controls.unitPrice;
      const totalRowControl = this.form.controls.totalRow;

      let total = 0;

      if (quantityControl) {
        quantityControl.valueChanges
          .subscribe(res => {
            total = res * unitPriceControl.value;
            totalRowControl.setValue(total);
          });
      }
      if (unitPriceControl) {
        unitPriceControl.valueChanges
          .subscribe(res => {
            total = quantityControl.value * res;
            totalRowControl.setValue(total);
          });
      }
    }
  }
}
